import { CODE_SIZE, initialCodeArray } from "constants/login"
import { confirmLoginCode } from "api"
import { saveAuthTokens } from "context/auth-token"
import { steps, StepTypes } from "./Login"
import BodyText from "components/Typography/BodyText"
import Button from "components/EvtKit/button/Button"
import Heading from "components/Typography/Heading"
import InputCode from "./components/InputCode"
import ResendButtonWithTimer from "./components/ResendButtonWithTimer"
import Toast from "components/EvtKit/Toast/ToastComponent"
import useHandleApiError from "hooks/useHandleApiErrors"

import ArrowLeftIcon from "components/icons/ArrowLeftIcon"
import ArrowRightIcon from "components/icons/ArrowRightIcon"
import EmailIcon from "components/icons/EmailIcon"

import { Dispatch, memo, SetStateAction, useCallback, useMemo, useState } from "react"
import { useMutation } from "react-query"
import { useNavigate } from "react-router-dom"
import clsx from "clsx"
import styled from "styled-components"

const Container = styled.div`
  width: 430px;
  min-height: 540px;
  box-shadow: 0px 2px 10px 0px #0000004d;
  border-radius: 20px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const IconBackContainer = styled.div`
  width: 100%;
  display: flex;
`

const IconEmailContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const HeadingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const CodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`

const ResendCodeContainer = styled.div`
  width: 100%;
  height: 51px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  box-shadow: 0px 1px 6px 0px #0000001a;
  background-color: ${(props) => props.theme.palette.gray_ice};
  border-radius: 8px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  height: 65px;
  justify-content: space-between;
`

interface ConfirmLoginProps {
  token?: string
  email: string
  setStep: Dispatch<SetStateAction<StepTypes>>
  setToken: Dispatch<SetStateAction<string | undefined>>
}

const ConfirmLogin = (props: ConfirmLoginProps) => {
  const { token = "", email, setStep, setToken } = props

  const [codeArray, setCodeArray] = useState<string[]>(initialCodeArray)
  const [error, setError] = useState<string | undefined>()
  const [focusedInput, setFocusedInput] = useState<number>(-1)
  const [resentEmail, setResentEmail] = useState<boolean>(false)

  const navigate = useNavigate()

  const { getErrorMessage } = useHandleApiError()

  const submitConfirmation = useMutation({
    mutationFn: async (data: App.ConfirmLoginPayload) => {
      const result = await confirmLoginCode(data)

      return result
    },
    onSuccess: (token) => {
      saveAuthTokens({ token })

      navigate("/home")
    },
    onError: (error) => {
      const errMsg = getErrorMessage(error, "Código inválido.")

      setError(errMsg)
    }
  })

  const handlePaste = useCallback(
    (pastedText: string) => {
      const digits = pastedText.replace(/\D/g, "").slice(0, codeArray.length).split("")

      const newCode = Array.from({ length: codeArray.length }, (_, i) => digits[i] || "")

      setCodeArray(newCode)

      const nextFocus = digits.length >= codeArray.length ? codeArray.length - 1 : digits.length

      setFocusedInput(nextFocus)
    },
    [codeArray.length]
  )

  const handleConfirmation = useCallback(() => {
    setError(undefined)

    const code = codeArray.join("")

    submitConfirmation.mutate({ code, token, email })
  }, [codeArray, token, email])

  const isButtonDisabled = useMemo(() => {
    const invalidCode = codeArray.some((value) => !value)

    return invalidCode || submitConfirmation.isLoading
  }, [codeArray, submitConfirmation.isLoading])

  const backToLoginForm = useCallback(() => {
    setStep(steps.login)

    setToken("")
  }, [])

  const formatEmail = useMemo(() => {
    const [emailName, emailType] = email.split("@")

    if (!emailName || !emailType) return ""

    const size = Math.round(emailName.length / 3)

    const firstPart = emailName.substring(0, 2)
    const secondPart = emailName.substring(size * 2, emailName.length)

    return firstPart + "***" + secondPart + "@" + emailType
  }, [email])

  const handleInputChange = useCallback(
    (id: number, newValue: string) => {
      setCodeArray((prevArray) => {
        return prevArray.map((prevValue, index) => {
          if (index === id) {
            return newValue
          }

          return prevValue
        })
      })
    },
    [codeArray]
  )

  return (
    <>
      {!!error && <Toast variant="error">{error}</Toast>}
      {resentEmail && <Toast>Código reenviado.</Toast>}
      <Container>
        <IconBackContainer>
          <button onClick={backToLoginForm}>
            <ArrowLeftIcon />
          </button>
        </IconBackContainer>

        <IconEmailContainer>
          <EmailIcon />
        </IconEmailContainer>

        <HeadingContainer>
          <Heading variant="h4">Um email foi enviado para você</Heading>
        </HeadingContainer>

        <CodeContainer>
          <BodyText size="semi-medium">
            Digite aqui o código de {CODE_SIZE} dígitos que você recebeu no e-mail cadastrado <b>{formatEmail}</b>{" "}
            (verifique também sua caixa de spam)
          </BodyText>

          <InputContainer>
            {codeArray.map((digit, index) => (
              <InputCode
                key={index}
                identifier={index}
                value={digit}
                focusedId={focusedInput}
                setFocusedInput={setFocusedInput}
                handleInputChange={handleInputChange}
                onPaste={index === 0 ? handlePaste : undefined}
              />
            ))}
          </InputContainer>

          <ResendCodeContainer>
            <BodyText size="medium" weight="semi-bold">
              Não recebeu o código?
            </BodyText>

            <ResendButtonWithTimer email={email} token={token} setError={setError} setResentEmail={setResentEmail} />
          </ResendCodeContainer>

          <Button
            disabled={isButtonDisabled}
            className={clsx("fill-width", { disabled: isButtonDisabled })}
            onClick={handleConfirmation}
            label="Validar"
            loading={submitConfirmation.isLoading}
            iconRight={<ArrowRightIcon />}
          />
        </CodeContainer>
      </Container>
    </>
  )
}

export default memo(ConfirmLogin)
